import { memo, useCallback, useId, useMemo, useRef, useState } from 'react'
import type { FocusEvent, KeyboardEvent, MouseEvent } from 'react'
import { useStore } from '../core/store'
import type { Session } from '../core/types'
import { treeItemName, treeRows } from './treeRows'
import { focusCardTitle } from '../ui/cardFocus'
import { openDetails } from '../ui/cardDetails'

/**
 * Which node the keyboard is on in the tree, for the canvas's focus ring.
 *
 * A module value rather than store state, for `screen.ts`'s reason: the only
 * reader is the caption layer's frame tick (`Labels.tsx`), which reads it per
 * pass and never re-renders, and a store write per arrow press would re-render
 * every subscriber for a ring nobody else draws. The same focus also HOVERS the
 * node, which the store does carry — that is what lights it and names it on the
 * canvas, and what tells the tick that something moved.
 */
export const treeFocus: { id: string | null } = { id: null }

/**
 * One row. Presentational only — every handler sits on the tree and finds its
 * row by `data-node` — and given primitives rather than the row object, which
 * is rebuilt on every revision: a memoised row re-renders when its own words,
 * place or state change, so a revision that touched one node reconciles one row.
 */
const TreeItem = memo(function TreeItem({
  id,
  level,
  setSize,
  posInSet,
  expanded,
  name,
  selected,
  tabbable,
  register,
}: {
  id: string
  level: number
  setSize: number
  posInSet: number
  expanded: boolean | null
  name: string
  selected: boolean
  tabbable: boolean
  register: (id: string, el: HTMLLIElement | null) => void
}) {
  return (
    <li
      ref={(el) => register(id, el)}
      data-node={id}
      role="treeitem"
      aria-level={level}
      aria-setsize={setSize}
      aria-posinset={posInSet}
      aria-expanded={expanded ?? undefined}
      aria-selected={selected}
      tabIndex={tabbable ? 0 : -1}
    >
      {name}
    </li>
  )
})

/**
 * The graph for the keyboard and the screen reader: one tree, one tab stop.
 *
 * The standard tree pattern (WAI-ARIA APG): the arrows walk it, Right and Left
 * open and close a branch or step in and out, Home and End jump to its ends,
 * Enter or Space opens the node in the thread, and focus follows it to the
 * card's title. Only one row is ever in the tab order (roving tabindex) — the
 * row last focused, else the selection, else the seed — so Tab enters the graph
 * once and the next Tab leaves it. The flat list before it put every node in the
 * tab order, and a keyboard took 28 presses to get past a graph it could not see.
 *
 * Opening and closing ARE the canvas fold (`toggleCollapse`, the same act as
 * `h` and ⌥-click), because the tree lists exactly what the canvas draws: a
 * folded branch is a closed row, and opening the row unfolds the branch.
 *
 * Focus is where a sighted keyboard user is, and this list is visually hidden,
 * so focus shows on the CANVAS: the row's node is hovered (lit, and its caption
 * promoted past the declutter), a ring is drawn round it (`Labels.tsx`), and
 * the camera eases to it when it is outside the visible field (`lookAt`). The
 * ease is a request the render loop answers only for a node out of view, so
 * walking through nodes already on screen moves nothing.
 */
export function NodeTree({
  session,
  visible,
  childrenOf,
  folded,
}: {
  session: Session
  visible: ReadonlySet<string>
  childrenOf: ReadonlyMap<string, readonly string[]>
  folded: ReadonlyMap<string, number>
}) {
  const selectedId = useStore((s) => s.selectedId)
  const helpId = useId()
  const rows = useMemo(
    () => treeRows(session, visible, childrenOf, folded),
    [session, visible, childrenOf, folded],
  )
  const [active, setActive] = useState<string | null>(null)
  const els = useRef(new Map<string, HTMLLIElement>())
  const register = useCallback((id: string, el: HTMLLIElement | null) => {
    if (el) els.current.set(id, el)
    else els.current.delete(id)
  }, [])

  const has = (id: string | null) => id !== null && rows.some((r) => r.id === id)
  const tabbable = has(active) ? active : has(selectedId) ? selectedId : (rows[0]?.id ?? null)

  const rowOf = (target: EventTarget) => {
    const id = (target as HTMLElement).closest?.('[data-node]')?.getAttribute('data-node')
    const i = id ? rows.findIndex((r) => r.id === id) : -1
    return i < 0 ? null : { row: rows[i]!, i }
  }

  const go = (id: string | undefined) => {
    if (!id) return
    setActive(id)
    els.current.get(id)?.focus()
  }

  // Opening a node takes the reader to it: focus goes to its card's title, where
  // reading the card begins. It used to stay on the row, and the card the reader
  // had asked for was a landmark away with nothing said about it. Tab comes back
  // to this same row, which keeps the tree's one tab stop.
  const open = (id: string) => {
    const st = useStore.getState()
    st.select(id)
    st.setRail(true)
    st.lookAt(id)
    // On a desk the card is the field's until its details are asked for (D354);
    // Enter is that ask, so the reader still lands on the card it promised.
    openDetails(id)
    focusCardTitle(id)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const at = rowOf(e.target)
    if (!at || e.metaKey || e.ctrlKey || e.altKey) return
    const { row, i } = at
    const st = useStore.getState()
    switch (e.key) {
      case 'ArrowDown':
        go(rows[i + 1]?.id)
        break
      case 'ArrowUp':
        go(rows[i - 1]?.id)
        break
      case 'Home':
        go(rows[0]?.id)
        break
      case 'End':
        go(rows[rows.length - 1]?.id)
        break
      case 'ArrowRight':
        // Closed: open it, and stay — the pattern's rule, and the reader hears
        // the row turn "expanded" before walking in. Open: step to the first
        // child, which is the next row in reading order.
        if (row.expanded === false) st.toggleCollapse(row.id)
        else if (row.expanded === true) go(rows[i + 1]?.id)
        break
      case 'ArrowLeft':
        // Open: close it. The seed cannot fold (folding the seed folds the app),
        // so on the seed this is a step nowhere, as the pattern allows at the top.
        if (row.expanded === true && row.parentId !== null) st.toggleCollapse(row.id)
        else go(row.parentId ?? undefined)
        break
      case 'Enter':
      case ' ':
        open(row.id)
        break
      default:
        return
    }
    // Taken here and stopped here. App's window handler binds the arrows to the
    // coach's list, Space to the replay and Enter to the coach's cue — a press
    // meant for the tree must not also walk a cue or start a replay behind it.
    // Every other key still reaches it, so the verbs work on the selection from
    // the tree exactly as they do from the canvas.
    e.preventDefault()
    e.stopPropagation()
  }

  const onFocus = (e: FocusEvent<HTMLUListElement>) => {
    const at = rowOf(e.target)
    if (!at) return
    setActive(at.row.id)
    treeFocus.id = at.row.id
    const st = useStore.getState()
    st.hover(at.row.id)
    st.lookAt(at.row.id)
  }

  const onBlur = () => {
    treeFocus.id = null
    useStore.getState().hover(null)
  }

  const onClick = (e: MouseEvent<HTMLUListElement>) => {
    // A screen reader's own "activate" arrives as a click on the row.
    const at = rowOf(e.target)
    if (at) open(at.row.id)
  }

  return (
    // Focusable and never a tab stop: the landing for a door that must put focus
    // at the graph without choosing a row — a row's focus eases the camera — or
    // raising a phone's keyboard (`focusNodeList` in ui/cardFocus.ts).
    <nav className="sr-only" aria-label="Nodes in this exploration" tabIndex={-1} data-node-list>
      <p id={helpId}>
        Each node is listed under the claim it answers. Arrow keys move between nodes; Right and
        Left open and close a branch. Enter opens a node’s card in the thread and moves you to it.
        Press Command-K (or Control-K) to search every node and run commands.
      </p>
      <ul
        role="tree"
        aria-label="Nodes, by branch"
        aria-describedby={helpId}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        onClick={onClick}
      >
        {rows.map((row) => {
          const n = session.nodes[row.id]!
          return (
            <TreeItem
              key={row.id}
              id={row.id}
              level={row.level}
              setSize={row.setSize}
              posInSet={row.posInSet}
              expanded={row.expanded}
              name={treeItemName(n, row, session)}
              selected={row.id === selectedId}
              tabbable={row.id === tabbable}
              register={register}
            />
          )
        })}
      </ul>
    </nav>
  )
}
